/*
 * Project: bellcloud
 * File: apps/frontend/src/components/device/connectDevice/DeviceImage.tsx
 * Description: Device image component with circular background
 * Uses global connection store - no props needed
 */

import React from 'react';
import styled from '@emotion/styled';
import { useConnectionStore } from '../../../store/connectionStore';
import { MINI_SENSOR_TYPE_MAP, MiniSensorType } from '@packages/types/device/mini/sensors';
import {
  MINI_SENSOR_IMAGE_MAP,
  MINI_IMAGE_DISCONNECTED,
  LITE_IMAGE_WHOLE,
  MINI_IMAGE_DEFAULT,
} from '@/utils/deviceImageUtils';
import { DeviceType } from '@packages/types/device/common/deviceTypes';

interface DeviceImageProps {
  /** Device type - optional, defaults to LITE */
  deviceType?: DeviceType;
}

/**
 * DeviceImage Component
 *
 * Displays device image based on connection state:
 * - Lite: connected/disconnected images
 * - Mini: sensor-specific images when connected
 * - Circular background with padding
 * - Automatically reads from useConnectionStore
 */
export const DeviceImage: React.FC<DeviceImageProps> = ({ deviceType = DeviceType.LITE }) => {
  const { device } = useConnectionStore();

  // Device is "connected" when device state is confirmed (type !== 'none')
  const isConnected = device.type !== 'none';

  /**
   * Get sensor-specific image for Mini device
   */
  const getMiniSensorImage = (): string => {
    const deviceInfo = device.type !== 'none' ? device.info : null;
    const equippedSensor = (deviceInfo as any)?.equipped_sensor;

    if (!equippedSensor) {
      return MINI_IMAGE_DEFAULT;
    }

    let sensorType: MiniSensorType;
    if (typeof equippedSensor === 'string') {
      sensorType = MINI_SENSOR_TYPE_MAP[equippedSensor] || MiniSensorType.CO;
    } else {
      sensorType = equippedSensor as MiniSensorType;
    }

    return MINI_SENSOR_IMAGE_MAP[sensorType] ?? MINI_IMAGE_DEFAULT;
  };

  /**
   * Select appropriate image based on device type and connection state
   */
  const getDeviceImage = (): string => {
    if (deviceType === DeviceType.MINI) {
      if (isConnected) {
        return getMiniSensorImage();
      }
      return MINI_IMAGE_DISCONNECTED;
    }

    // Lite device: always show full image (connect page uses whole image regardless of state)
    return LITE_IMAGE_WHOLE;
  };

  const getAltText = (): string => {
    const deviceName = deviceType === DeviceType.MINI ? 'BELLMINI' : 'BELLLITE';
    const state = isConnected ? 'connected' : 'disconnected';
    return `${deviceName} ${state}`;
  };

  return (
    <ImageContainer $isConnected={isConnected}>
      <DeviceImg src={getDeviceImage()} alt={getAltText()} $isConnected={isConnected} />
    </ImageContainer>
  );
};

// ==================== Styled Components ====================

const ImageContainer = styled.div<{ $isConnected: boolean }>`
  width: 114px;
  height: 114px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.full}px;
  padding: ${({ theme }) => theme.spacing[5]}px;
  background-color: ${({ theme, $isConnected }) =>
    $isConnected
      ? theme.colors.surface.interactive.hover
      : theme.colors.surface.group?.default || 'rgba(241, 241, 241, 1)'};
`;

const DeviceImg = styled.img<{ $isConnected: boolean }>`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
  opacity: ${({ $isConnected }) => ($isConnected ? 1 : 0.7)};
  transition: opacity 0.3s ease;
`;
